type AnalyticsParams = Record<string, string | number | boolean | null | undefined>;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    __ludoAnalyticsHistory?: Array<{ name: string; params: AnalyticsParams; ts: number }>;
    __ludoAnalyticsSessionStartedAt?: number;
  }
}

const measurementId = (import.meta.env.VITE_GA_MEASUREMENT_ID ?? "").trim();

function pushToHistory(name: string, params: AnalyticsParams) {
  if (!window.__ludoAnalyticsHistory) {
    window.__ludoAnalyticsHistory = [];
  }
  window.__ludoAnalyticsHistory.push({ name, params, ts: Date.now() });
}

export function initAnalytics() {
  if (!measurementId) {
    if (!window.__ludoAnalyticsHistory) {
      window.__ludoAnalyticsHistory = [];
    }
    return;
  }

  if (window.gtag) {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  const gtagFn = (...args: unknown[]) => {
    window.dataLayer?.push(args);
  };
  window.gtag = gtagFn;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  gtagFn("js", new Date());
  gtagFn("config", measurementId, { send_page_view: true });

  if (!window.__ludoAnalyticsHistory) {
    window.__ludoAnalyticsHistory = [];
  }
}

export function trackEvent(name: string, params: AnalyticsParams = {}) {
  const safeParams: AnalyticsParams = Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null)
  );

  pushToHistory(name, safeParams);

  if (!measurementId || !window.gtag) {
    return safeParams;
  }

  window.gtag("event", name, safeParams);
  return safeParams;
}

export function trackPageView(screen: string, title?: string) {
  trackEvent("page_view", {
    screen,
    page_title: title ?? document.title,
    page_location: window.location.href,
  });
}

export function startSessionTimer() {
  if (window.__ludoAnalyticsSessionStartedAt) {
    return;
  }
  window.__ludoAnalyticsSessionStartedAt = Date.now();
}

export function flushSessionDuration() {
  const start = window.__ludoAnalyticsSessionStartedAt ?? Date.now();
  const durationSeconds = Math.max(1, Math.round((Date.now() - start) / 1000));
  trackEvent("session_duration", { duration_seconds: durationSeconds });
  window.__ludoAnalyticsSessionStartedAt = Date.now();
}

export function getAnalyticsHistory() {
  if (!window.__ludoAnalyticsHistory) {
    window.__ludoAnalyticsHistory = [];
  }
  return window.__ludoAnalyticsHistory;
}
